import en from "./image/260-united-kingdom.svg";
import la from "./image/112-laos.svg";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n/lang";
import { useSelector, useDispatch } from "react-redux";
import { addLang, addLangLabel } from "../../../redux/langSlice";

const options = [
  {
    value: "en",
    label: en,
    code: "lang.eng",
  },
  {
    value: "la",
    label: la,
    code: "lang.la",
  },
];

const getStoredOption = () => {
  const savedCode = localStorage.getItem("lang") || "la";
  return options.find((item) => item.value === savedCode) || options[1];
};

const DropdownLangMobile = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { languages } = useSelector((state) => state.languages);

  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(getStoredOption);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 8 });

  const currentCode = languages?.lang || selected.value;
  const currentOption = useMemo(
    () => options.find((item) => item.value === currentCode) || selected,
    [currentCode, selected]
  );

  const updatePanelPosition = useCallback(() => {
    if (!triggerRef.current || typeof window === "undefined") return;

    const rect = triggerRef.current.getBoundingClientRect();
    const panelWidth = 184;
    const viewportPadding = 8;
    const maxLeft = Math.max(viewportPadding, window.innerWidth - panelWidth - viewportPadding);
    const left = Math.min(
      Math.max(viewportPadding, rect.right - panelWidth),
      maxLeft
    );

    setPanelPosition({
      top: Math.round(rect.bottom + 8),
      left: Math.round(left),
    });
  }, []);

  useEffect(() => {
    const initialOption = getStoredOption();
    setSelected(initialOption);
    dispatch(addLang(initialOption.value));
    dispatch(addLangLabel(initialOption));
    i18n.changeLanguage(initialOption.value);
  }, [dispatch]);

  useEffect(() => {
    if (!isOpen) return undefined;

    updatePanelPosition();

    const handleOutside = (event) => {
      const target = event.target;
      if (
        triggerRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    const handleViewportChange = () => updatePanelPosition();

    document.addEventListener("pointerdown", handleOutside);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("orientationchange", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      document.removeEventListener("pointerdown", handleOutside);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("orientationchange", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [isOpen, updatePanelPosition]);

  const handleSelect = (option) => {
    setSelected(option);
    localStorage.setItem("lang", option.value);
    localStorage.setItem("langLabel", JSON.stringify(option));
    dispatch(addLang(option.value));
    dispatch(addLangLabel(option));
    i18n.changeLanguage(option.value);
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const panel = isOpen ? (
    <div
      ref={panelRef}
      className="gwin-mobile-language-panel"
      role="menu"
      aria-label={`${t("lang.la")} / ${t("lang.eng")}`}
      style={{ top: panelPosition.top, left: panelPosition.left }}
    >
      {options.map((item) => {
        const active = currentCode === item.value;
        return (
          <button
            key={item.value}
            type="button"
            role="menuitemradio"
            aria-checked={active}
            className={`gwin-mobile-language-option${active ? " is-active" : ""}`}
            onClick={() => handleSelect(item)}
          >
            <img src={item.label} width="26" height="26" alt="" />
            <span>{t(item.code)}</span>
            {active && <i className="la la-check" aria-hidden="true" />}
          </button>
        );
      })}
    </div>
  ) : null;

  return (
    <div className="gwin-mobile-language-switcher">
      <button
        ref={triggerRef}
        type="button"
        className={`gwin-mobile-language-trigger${isOpen ? " is-open" : ""}`}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={`${t("lang.la")} / ${t("lang.eng")}`}
        onClick={() => {
          if (!isOpen) updatePanelPosition();
          setIsOpen((open) => !open);
        }}
      >
        <img src={currentOption.label} width="24" height="24" alt="" />
        <i className="la la-angle-down" aria-hidden="true" />
      </button>

      {typeof document !== "undefined" && panel
        ? createPortal(panel, document.body)
        : null}
    </div>
  );
};

export default DropdownLangMobile;
